import type { LocalUserChoices } from '@livekit/components-react';
import type { JoinContextResponse } from '@hiredude/meet-shared';
import {
  ArrowRight,
  CalendarClock,
  Camera,
  CameraOff,
  Circle,
  Mic,
  MicOff,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { BrandMark } from '@/components/BrandMark';
import { DeviceMenu } from '@/components/DeviceMenu';
import { MicMeter } from '@/components/MicMeter';
import { VideoPreview } from '@/components/prejoin/VideoPreview';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAudioLevel } from '@/hooks/useAudioLevel';
import { useMediaPreview } from '@/hooks/useMediaPreview';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { formatIstDateTime } from '@/lib/istClock';
import { cn } from '@/lib/utils';

/**
 * The green room.
 *
 * Replaces LiveKit's `PreJoin` prefab so the screen can carry the things that
 * actually settle a nervous candidate: who they are about to meet, whether
 * they are being recorded, and visible proof that the microphone works.
 */
export function PreJoinScreen({
  context,
  connecting,
  onSubmit,
}: {
  context: JoinContextResponse;
  connecting: boolean;
  onSubmit: (choices: LocalUserChoices) => void;
}) {
  // Once the choices are handed over, the preview must not re-open anything:
  // LiveKit is about to claim the same camera, and some drivers refuse a
  // second handle on it.
  const [handedOver, setHandedOver] = useState(false);
  const preview = useMediaPreview(!handedOver);
  const [name, setName] = useState(context.displayName);
  const level = useAudioLevel(preview.audioTrack, preview.microphoneEnabled);

  const isHost = context.role === 'host';

  const submit = () => {
    setHandedOver(true);
    preview.release();
    onSubmit({
      username: name.trim() || context.displayName,
      videoEnabled: preview.cameraEnabled,
      audioEnabled: preview.microphoneEnabled,
      videoDeviceId: preview.cameraId,
      audioDeviceId: preview.microphoneId,
    });
  };

  const startsAtLabel = useMemo(() => formatIstDateTime(context.startsAt), [context.startsAt]);

  // A phone in either orientation: narrower than Tailwind's `sm`, or a
  // landscape screen too short to show the card and the button together.
  const phone = useMediaQuery('(max-width: 639px), (max-height: 500px)');

  // Pinned to the bottom of a phone screen so joining never needs a scroll;
  // part of the card's flow from `sm` up.
  const joinFooter = (
    <div
      className={
        phone
          ? 'bg-card/90 fixed inset-x-0 bottom-0 z-30 space-y-2.5 border-t px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur-xl'
          : 'mt-auto space-y-3 pt-1'
      }
    >
      <Button
        type="button"
        size="lg"
        onClick={submit}
        disabled={connecting}
        className="lk-join-button w-full"
      >
        {connecting ? (
          <>
            <Spinner className="size-4" />
            Connecting…
          </>
        ) : (
          <>
            Join interview
            <ArrowRight className="size-4" />
          </>
        )}
      </Button>

      <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-center text-xs text-pretty">
        {connecting ? (
          <>
            <Sparkles className="size-3.5" />
            Getting you into the room…
          </>
        ) : (
          <>
            <ShieldCheck className="size-3.5" />
            Private link — only you and {context.otherPartyName} can join
          </>
        )}
      </p>
    </div>
  );

  return (
    // On a phone the join button lives in a fixed footer, so the page reserves
    // room beneath the card for it rather than letting it cover the devices.
    <main
      className={cn(
        'prejoin-shell grid min-h-svh place-items-start p-3 sm:place-items-center sm:p-6',
        phone && 'pb-36 sm:pb-32',
      )}
    >
      <div className="w-full max-w-5xl">
        <BrandMark className="mb-3 px-1 sm:mb-5" size="sm" />

        <Card className="prejoin-card animate-in fade-in-0 slide-in-from-bottom-3 overflow-hidden p-0 duration-500 lg:grid lg:grid-cols-[1.3fr_1fr]">
          {/* ---------- preview ---------- */}
          <div className="relative">
            <VideoPreview
              track={preview.videoTrack}
              status={preview.status}
              problem={preview.problem}
              cameraEnabled={preview.cameraEnabled}
              displayName={name || context.displayName}
            >
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <MediaToggle
                    on={preview.microphoneEnabled}
                    onLabel="Mute microphone"
                    offLabel="Unmute microphone"
                    OnIcon={Mic}
                    OffIcon={MicOff}
                    onClick={() => preview.setMicrophoneEnabled(!preview.microphoneEnabled)}
                    disabled={preview.status !== 'ready'}
                  />
                  <MediaToggle
                    on={preview.cameraEnabled}
                    onLabel="Turn camera off"
                    offLabel="Turn camera on"
                    OnIcon={Camera}
                    OffIcon={CameraOff}
                    onClick={() => preview.setCameraEnabled(!preview.cameraEnabled)}
                    disabled={preview.status !== 'ready'}
                  />
                </div>

                <div className="flex items-center gap-2 rounded-full bg-black/45 px-3 py-2 backdrop-blur-sm">
                  {preview.microphoneEnabled ? (
                    <Mic className="size-3.5 text-white/70" />
                  ) : (
                    <MicOff className="size-3.5 text-white/40" />
                  )}
                  <MicMeter level={level} muted={!preview.microphoneEnabled} />
                </div>
              </div>
            </VideoPreview>
          </div>

          {/* ---------- details ---------- */}
          <div className="flex flex-col gap-4 p-4 sm:gap-5 sm:p-7">
            <header className="space-y-1.5 sm:space-y-2">
              <h1 className="text-xl font-semibold tracking-tight text-balance sm:text-2xl">
                {context.title}
              </h1>
              <p className="text-muted-foreground text-sm text-pretty">
                {isHost
                  ? `You are the interviewer. ${context.otherPartyName} has the same link.`
                  : `Your interview with ${context.otherPartyName}.`}
              </p>
            </header>

            <div className="space-y-2">
              {startsAtLabel && (
                <InfoRow
                  icon={CalendarClock}
                  tone="info"
                  title={`Interview starts at ${startsAtLabel}`}
                  body="You can join now. This is the scheduled start, not a lock on the link."
                />
              )}
              {context.recordingEnabled && (
                <InfoRow
                  icon={Circle}
                  tone="record"
                  title="This call will be recorded"
                  body="The recording is shared with the hiring team."
                />
              )}
            </div>

            <Separator />

            <div className="space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <Label htmlFor="display-name">Your name</Label>
                <Input
                  id="display-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="How should we show your name?"
                  maxLength={60}
                  autoComplete="name"
                />
              </div>

              {/* Side by side on a phone and a tablet, where height is the
                  scarce thing; stacked in the narrow desktop column. */}
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
                <DeviceMenu
                  icon={Mic}
                  label="Microphone"
                  devices={preview.microphones}
                  activeDeviceId={preview.microphoneId}
                  onSelect={preview.selectMicrophone}
                  disabled={preview.status !== 'ready'}
                />
                <DeviceMenu
                  icon={Camera}
                  label="Camera"
                  devices={preview.cameras}
                  activeDeviceId={preview.cameraId}
                  onSelect={preview.selectCamera}
                  disabled={preview.status !== 'ready'}
                />
              </div>
            </div>

            {/* Card's backdrop-filter traps `position: fixed`, so the phone
                footer is portalled out of it rather than styled in place. */}
            {phone ? createPortal(joinFooter, document.body) : joinFooter}
          </div>
        </Card>
      </div>
    </main>
  );
}

/** Circular camera/mic toggle. Red when off, which is the universal signal. */
function MediaToggle({
  on,
  onLabel,
  offLabel,
  OnIcon,
  OffIcon,
  onClick,
  disabled,
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
  OnIcon: typeof Mic;
  OffIcon: typeof MicOff;
  onClick: () => void;
  disabled?: boolean;
}) {
  const label = on ? onLabel : offLabel;
  const Icon = on ? OnIcon : OffIcon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          aria-pressed={!on}
          className={cn(
            'grid size-12 place-items-center rounded-full border transition-[background,transform] duration-150 active:scale-95 sm:size-11',
            'focus-visible:outline-ring/70 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-40',
            on
              ? 'border-white/15 bg-white/12 text-white backdrop-blur-sm hover:bg-white/20'
              : 'bg-destructive border-transparent text-white hover:brightness-110',
          )}
        >
          <Icon className="size-4.5" strokeWidth={2} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

function InfoRow({
  icon: Icon,
  tone,
  title,
  body,
}: {
  icon: typeof Circle;
  tone: 'record' | 'warn' | 'info';
  title: string;
  body: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-xl border px-3 py-2 sm:items-start sm:gap-3 sm:p-3',
        tone === 'record'
          ? 'border-[color-mix(in_oklab,var(--destructive)_30%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_10%,transparent)]'
          : tone === 'warn'
            ? 'border-[color-mix(in_oklab,var(--warning)_30%,transparent)] bg-[color-mix(in_oklab,var(--warning)_10%,transparent)]'
            : 'border-border bg-muted/40',
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0 sm:mt-0.5',
          tone === 'record'
            ? 'fill-current text-[color-mix(in_oklab,var(--destructive)_80%,white)]'
            : tone === 'warn'
              ? 'text-[var(--warning)]'
              : 'text-foreground/70',
        )}
      />
      <div className="space-y-0.5">
        <p className="text-[13px] leading-tight font-medium sm:text-sm">{title}</p>
        {/* A one-line chip on a phone; the explanation is for larger screens. */}
        <p className="text-muted-foreground hidden text-xs text-pretty sm:block">{body}</p>
      </div>
    </div>
  );
}
